// Экспорт табличных данных: CSV (для Excel) и печатный реестр (HTML → печать).
import { openDocWindow } from './deals-api';
import { money } from './format';

export type Col<T> = {
  header: string;
  value: (row: T) => string | number; // значение для CSV (сырое)
  print?: (row: T) => string | number; // значение для реестра (по умолчанию = value)
  align?: 'r'; // выравнивание вправо в реестре (для чисел)
};

const stamp = () => new Date().toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
const dateForFile = () => new Date().toISOString().slice(0, 10);

function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[";\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// Скачать CSV. Разделитель «;» и BOM — чтобы Excel (RU/KZ) открыл с кириллицей.
export function downloadCSV<T>(filename: string, cols: Col<T>[], rows: T[]): void {
  const head = cols.map((c) => csvCell(c.header)).join(';');
  const body = rows.map((r) => cols.map((c) => csvCell(c.value(r))).join(';')).join('\r\n');
  const csv = '\uFEFF' + head + '\r\n' + body + '\r\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${dateForFile()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const esc = (v: unknown) =>
  String(v ?? '').replace(
    /[&<>]/g,
    (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[m] as string,
  );

// Открыть печатный реестр. totals — итоговая строка (по колонкам, '' где не нужно).
export function printRegister<T>(opts: {
  title: string;
  subtitle?: string;
  cols: Col<T>[];
  rows: T[];
  totals?: (string | number)[];
}): void {
  const { title, subtitle, cols, rows, totals } = opts;
  const thead = cols.map((c) => `<th class="${c.align ?? ''}">${esc(c.header)}</th>`).join('');
  const tbody = rows
    .map(
      (r) =>
        '<tr>' +
        cols
          .map((c) => `<td class="${c.align ?? ''}">${esc((c.print ?? c.value)(r))}</td>`)
          .join('') +
        '</tr>',
    )
    .join('');
  const tfoot = totals
    ? `<tfoot><tr>${cols.map((c, i) => `<td class="${c.align ?? ''}">${esc(totals[i] ?? '')}</td>`).join('')}</tr></tfoot>`
    : '';

  const html = `<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font: 12px/1.4 'Segoe UI', Arial, sans-serif; color: #111; margin: 24px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 8px; margin-bottom: 6px; }
  .head .co { font-weight: 800; font-size: 15px; }
  .head .co .sub { font-weight: 400; font-size: 11px; color: #555; }
  .head .ttl { text-align: right; font-size: 16px; font-weight: 700; }
  .meta { color: #555; font-size: 11px; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #bbb; padding: 5px 7px; text-align: left; }
  th { background: #f0f0f0; font-size: 11px; text-transform: uppercase; letter-spacing: .3px; }
  td.r, th.r { text-align: right; white-space: nowrap; }
  tfoot td { font-weight: 800; background: #fafafa; }
  tbody tr:nth-child(even) td { background: #fbfbfb; }
  .foot { margin-top: 12px; color: #777; font-size: 10px; }
  @media print { body { margin: 10mm; } @page { margin: 12mm; } }
</style></head>
<body onload="window.print()">
  <div class="head">
    <div class="co">ТОО «AZAM-KC»<div class="sub">Пульт завода · элеваторное оборудование</div></div>
    <div class="ttl">${esc(title)}</div>
  </div>
  ${subtitle ? `<div class="meta">${esc(subtitle)}</div>` : ''}
  <table><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody>${tfoot}</table>
  <div class="foot">Сформировано: ${esc(stamp())} · строк: ${rows.length}</div>
</body></html>`;
  openDocWindow(html);
}

export const registerMoney = (n: number | null | undefined): string => (n == null ? '' : money(n));
